'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { LiveBackground } from './LiveBackground'

/** Everything that counts as "clickable" for the click effect. */
const INTERACTIVE =
  'a[href], button:not([disabled]), [role="button"], [role="tab"], summary, select, label[for], input[type="submit"], input[type="button"], input[type="checkbox"], input[type="radio"]'
const MAGNETIC = '.btn-primary, .btn-secondary, [data-cta], [data-magnetic]'
const TEXT_INPUT =
  'input:not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]'
/** Cards that get a soft light following the cursor. */
const SPOT = 'a.rounded-3xl, a.rounded-2xl, .rounded-3xl.border, .rounded-2xl.border, [data-spot]'
/** Things that animate in when scrolled into view (every time, up or down). */
const REVEAL =
  'h1, h2, h3, h4, p, ul, ol, dl, img, svg[role="img"], figure, form, table, blockquote, .btn-primary, .btn-secondary, .rounded-2xl, .rounded-3xl, [class*="rounded-[2"], [data-reveal]'
const NO_REVEAL = 'header, nav, details, [data-no-reveal], .ncp-marquee, [role="dialog"], [aria-modal="true"]'

/**
 * Site-wide "experience" layer. It only draws in fixed layers and uses
 * transforms/opacity, so it never moves or re-aligns page content.
 *  - calm live background (LiveBackground)
 *  - click ripple, magnetic main buttons, spotlight on cards, scroll progress bar
 *  - content animates in when it scrolls into view, each time (down and up)
 * Switched off on /admin and /portal, reduced for "reduce motion".
 */
export function ExperienceLayer() {
  const pathname = usePathname() ?? ''
  const off = /\/(admin|portal)(\/|$)/.test(pathname)
  const barRef = useRef<HTMLDivElement>(null)
  const fxRef = useRef<HTMLDivElement>(null)

  // ---- pointer effects, click ripple, progress bar
  useEffect(() => {
    if (off) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const bar = barRef.current
    const fx = fxRef.current
    let magnet: HTMLElement | null = null
    let spot: HTMLElement | null = null

    const releaseMagnet = () => {
      if (magnet) {
        magnet.style.translate = ''
        magnet = null
      }
    }

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      const target = e.target as Element | null
      const m = !reduced ? ((target?.closest?.(MAGNETIC) as HTMLElement | null) ?? null) : null
      if (m !== magnet) releaseMagnet()
      if (m) {
        magnet = m
        const r = m.getBoundingClientRect()
        const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2)
        const dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2)
        m.style.translate = `${dx * 4}px ${dy * 3}px`
      }
      const s = (target?.closest?.(SPOT) as HTMLElement | null) ?? null
      if (s !== spot) {
        spot?.classList.remove('ncp-spot-on')
        spot = s
        if (s && getComputedStyle(s).position !== 'static') s.classList.add('ncp-spot', 'ncp-spot-on')
        else spot = null
      }
      if (spot) {
        const r = spot.getBoundingClientRect()
        spot.style.setProperty('--mx', `${e.clientX - r.left}px`)
        spot.style.setProperty('--my', `${e.clientY - r.top}px`)
      }
    }

    const onLeaveWindow = () => {
      releaseMagnet()
      spot?.classList.remove('ncp-spot-on')
      spot = null
    }

    // click ripple: a clean gold ring + 8 short rays
    const burst = (x: number, y: number) => {
      if (!fx || reduced) return
      const ring = document.createElement('span')
      ring.className = 'ncp-ripple'
      ring.style.left = `${x}px`
      ring.style.top = `${y}px`
      fx.appendChild(ring)
      ring
        .animate(
          [
            { transform: 'translate(-50%,-50%) scale(0.3)', opacity: 0.95 },
            { transform: 'translate(-50%,-50%) scale(2.2)', opacity: 0 },
          ],
          { duration: 520, easing: 'cubic-bezier(.2,.7,.2,1)' }
        )
        .finished.then(() => ring.remove(), () => ring.remove())
      for (let i = 0; i < 8; i++) {
        const ray = document.createElement('span')
        ray.className = 'ncp-ray'
        ray.style.left = `${x}px`
        ray.style.top = `${y}px`
        fx.appendChild(ray)
        const a = (360 / 8) * i + 22.5
        ray
          .animate(
            [
              { transform: `translate(-50%,-50%) rotate(${a}deg) translateY(-10px) scaleY(0.4)`, opacity: 1 },
              { transform: `translate(-50%,-50%) rotate(${a}deg) translateY(-34px) scaleY(1)`, opacity: 0 },
            ],
            { duration: 420, easing: 'cubic-bezier(.15,.75,.3,1)' }
          )
          .finished.then(() => ray.remove(), () => ray.remove())
      }
    }

    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null
      const inter = target?.closest?.(INTERACTIVE)
      if (inter && !target?.closest?.(TEXT_INPUT)) {
        burst(e.clientX, e.clientY)
      }
    }

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        const max = document.documentElement.scrollHeight - window.innerHeight
        const p = max > 0 ? Math.min(1, window.scrollY / max) : 0
        if (bar) bar.style.transform = `scaleX(${p})`
      })
    }
    onScroll()

    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerdown', onDown, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    document.documentElement.addEventListener('mouseleave', onLeaveWindow)
    return () => {
      releaseMagnet()
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      document.documentElement.removeEventListener('mouseleave', onLeaveWindow)
    }
  }, [off])

  // ---- scroll reveal (replays every time an element re-enters the screen)
  useEffect(() => {
    if (off) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    if (typeof IntersectionObserver === 'undefined' || !('animate' in Element.prototype)) return

    const tagged = new WeakSet<Element>()
    const holds = new WeakMap<Element, Animation>()
    const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

    const hide = (el: Element) => {
      holds.get(el)?.cancel()
      // only opacity while hidden: moving it would push it out of view and it would never come back
      holds.set(el, el.animate([{ opacity: 0 }], { duration: 1, fill: 'forwards' }))
    }

    const io = new IntersectionObserver(
      (entries) => {
        let order = 0
        for (const e of entries) {
          const el = e.target
          if (e.isIntersecting) {
            const fromAbove = e.boundingClientRect.top < 0
            holds.get(el)?.cancel()
            holds.delete(el)
            el.animate(
              [
                { opacity: 0, translate: `0 ${fromAbove ? -24 : 28}px`, scale: '0.985' },
                { opacity: 1, translate: '0 0', scale: '1' },
              ],
              { duration: 850, easing: EASE, delay: Math.min(order, 6) * 70, fill: 'backwards' }
            )
            order++
          } else if (e.rootBounds) {
            // left the screen: reset so it animates again next time (from the side it re-enters)
            hide(el)
          }
        }
      },
      // the 40px margin is larger than the slide distance, so the entrance animation
      // can never move an element out of the observed area (no flicker at the page bottom)
      { threshold: 0, rootMargin: '40px 0px 40px 0px' }
    )

    const scan = () => {
      const scope = document.querySelectorAll('main, footer')
      scope.forEach((root) => {
        root.querySelectorAll(REVEAL).forEach((el) => {
          if (tagged.has(el)) return
          if (el.closest(NO_REVEAL)) return
          // only the outermost match animates (no double animations inside cards)
          let p = el.parentElement
          while (p && p !== root) {
            if (tagged.has(p)) return
            p = p.parentElement
          }
          const cs = getComputedStyle(el)
          if (cs.position === 'fixed' || cs.position === 'sticky') return
          tagged.add(el)
          io.observe(el)
        })
      })
    }

    // wait a frame so the page has painted, then watch for new content (route changes, tabs)
    const t = window.setTimeout(scan, 60)
    let pending = 0
    const mo = new MutationObserver(() => {
      if (pending) return
      pending = window.setTimeout(() => {
        pending = 0
        scan()
      }, 200)
    })
    mo.observe(document.body, { childList: true, subtree: true })
    return () => {
      window.clearTimeout(t)
      window.clearTimeout(pending)
      mo.disconnect()
      io.disconnect()
    }
  }, [off, pathname])

  if (off) return null

  return (
    <>
      <LiveBackground />
      <div aria-hidden className="ncp-progress" ref={barRef} />
      <div aria-hidden className="ncp-fx" ref={fxRef} />
    </>
  )
}
